import * as React from 'react';
import { Col, Row } from 'antd';
import { observer } from 'mobx-react';
import { Prompt } from 'react-router';

import { TextView } from 'src/core/ui/text.view';
import { CardController } from 'src/app/shared/card.controller';
import { HeaderView } from 'src/app/shared/header.view';
import { TabLanguageController } from 'src/core/ui/language/tab-language.controller';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { TaxEditorController } from 'src/app/general/taxes/parts/tax-editor.controller';
import { BooleanView } from '../../../../core/ui/boolean.view';
import { PriceRoundingTypeEditor } from './parts/price-rounding-type-editor';

interface MarketFormViewProps extends ComponentViewProps<any> {
	title: string,
	save: () => void,
	cancel: () => void,
	model: DataSet<MarketItem>;
	mode: ControlMode;
	onModeChanged: (mode: ControlMode) => void;
	currencies: CurrencySummary[];
}

@observer
export class MarketFormView extends React.Component<MarketFormViewProps, any> {
	constructor(props: any) {
		super(props);
	}

	optionsMargins = [
		{ value: '0', label: 'Margin only from Market' },
		{ value: '1', label: 'Margin only from Supplier' },
		{ value: '2', label: 'Margin from Market + Supplier' }
	];

	render() {
		const model = this.props.model.item;
		if (model == null) return null;
		const mode = this.props.mode;
		const displayNames = model.dynamicProperties['displayName'];
		const array: TabLanguageModel[] = [{ title: 'Description', dynamicProperties: displayNames }];

		return (
			<div className="window window-normal">
				<Prompt
					when={this.props.model.isDirty}
					message={location => `If you go to ${location.pathname}, all changes will be lost. Are you sure you want to continue?`}
				/>
				<Row>
					<Col xs={24} lg={{ offset: 6, span: 12 }}>
						<Row>
							<Col xs={24}>
								<HeaderView
									title={this.props.title}
									mode={this.props.mode}
									onModeChanged={this.props.onModeChanged}
									save={this.props.save}
									cancel={this.props.cancel}
									contextService={this.props.model}
								/>
							</Col>
						</Row>
						{
							model != null &&
							<CardController
								mode={this.props.mode}
								icon="book"
								title="General information">
								<Row gutter={16}>
									<Col xs={24}>
										<TextView
											onGetErrorMessage={() => model.validate(false, 'name')}
											mode={mode}
											label="Name"
											value={model.name}
											onChanged={newVal => model.name = newVal}
											required
										/>
									</Col>
									<Col xs={24}>
										<TabLanguageController
											model={model}
											dynamicProperties={array}
											mode={mode}
											setBusy={this.props.setBusy}
											isRichTextBox={false}
										/>
									</Col>
									<Col xs={24}>
										<DropdownView
											label="Currency"
											required={true}
											model={(model) ? model.currencyId : null}
											keyField="id"
											textField="iso3Name"
											disabled={this.props.mode == 'display'}
											onChanged={(item) => {
												model.currencyId = parseInt(item.id)
											}}
											options={this.props.currencies}
										/>
									</Col>
									<Col xs={24}>
										<PriceRoundingTypeEditor
											required
											value={model.marketPriceType}
											mode={this.props.mode}
											onChange={(item) => {
												model.marketPriceType = item;
											}}
										/>
									</Col>
									<Col xs={24}>
										<TextView
											onGetErrorMessage={() => model.validate(false, 'defaultMargin')}
											required
											mode={mode}
											label='Default Margin'
											format='percent'
											value={model.defaultMargin}
											suffix='%'
											onChanged={newVal => {
												newVal = newVal != null ? parseFloat(newVal) : null;
												model.defaultMargin = newVal;
											}}
										/>
									</Col>
									<Col xs={24}>
										<TextView
											onGetErrorMessage={() => model.validate(false, 'depositPercent')}
											required
											mode={model.requiresPayment ? mode : "display"}
											label='Deposit percent'
											format='percent'
											value={model.depositPercent}
											suffix='%'
											onChanged={newVal => {
												newVal = newVal != null ? parseFloat(newVal) : null;
												model.depositPercent = newVal;
											}}
											addonBefore={<BooleanView
												value={model.requiresPayment}
												mode={this.props.mode}
												size="small"
												onChanged={newVal => model.requiresPayment = newVal}
												label={null}
											/>}
										/>
									</Col>
									<Col xs={24}>
										<TextView
											onGetErrorMessage={() => model.validate(false, 'invoiceDueDateInDays')}
											required
											mode={mode}
											label="Due date in days before quote's departure"
											value={model.invoiceDueDateInDays}
											onChanged={newVal => {
												newVal = newVal != null ? parseInt(newVal) : null;
												model.invoiceDueDateInDays = newVal;
											}}
										/>
									</Col>
									<Col xs={24}>
										<DropdownView
											label="Margin Policy"
											model={model.marginPolicy != null ? model.marginPolicy.toString() : ''}
											required
											disabled={this.props.mode == 'display'}
											onChanged={(item) => {
												model.marginPolicy = parseInt(item.value);
											}}
											options={this.optionsMargins}
										/>
									</Col>
									<Col xs={24}>
										<TaxEditorController
											label="Default tax"
											required
											mode={this.props.mode}
											value={model.defaultTaxId}
											onChanged={newVal => model.defaultTaxId = newVal.id}
											setBusy={this.props.setBusy}
										/>
									</Col>
								</Row>
							</CardController>
						}
					</Col>
				</Row>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/market/form/market-form.view.tsx